import {
  PagesAcknowledgePageDocument,
  PagesPageAcknowledgementStatusDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface UsePageAcknowledgementProps {
  pageId: number
  ackRequiredVersion?: number | null
  isMandatory?: boolean
}

export const usePageAcknowledgement = ({
  pageId,
  ackRequiredVersion,
  isMandatory,
}: UsePageAcknowledgementProps) => {
  const { employee } = useCurrentSession()

  const { data, loading, refetch } = useQuery(PagesPageAcknowledgementStatusDocument, {
    variables: {
      pageId,
      employeeId: Number(employee?.id),
    },
    skip: !employee?.id || !isMandatory,
  })

  const [acknowledgePage, { loading: acknowledging }] = useMutation(PagesAcknowledgePageDocument, {
    extractorFn: (result) =>
      result.handbookPagesMutations.acknowledgeHandbookpagesPageAcknowledgement,
  })

  const latestAcknowledgement = data?.handbookPages.pageAcknowledgementsConnection.nodes[0]
  const pageAckVersion = data?.handbookPages.page?.ackRequiredVersion ?? ackRequiredVersion

  // Check if user has already acknowledged the required version
  const hasAcknowledged =
    latestAcknowledgement &&
    pageAckVersion !== undefined &&
    pageAckVersion !== null &&
    latestAcknowledgement.acknowledgedVersion >= pageAckVersion

  const handleAcknowledge = async () => {
    const result = await acknowledgePage({
      variables: {
        pageId,
      },
    })

    const handler = handleResult(result, () => null)

    if (handler.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('pages.page.acknowledgement.success'),
      })
      refetch()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    }
  }

  return {
    loading,
    acknowledging,
    hasAcknowledged: !!hasAcknowledged,
    pageAckVersion,
    handleAcknowledge,
  }
}
